import AgreementSignModal from '@/components/config_components/AgreementSignModal/AgreementSignModal.vue';
/* eslint-disable arrow-body-style */
import {
  hide_onboarding_system_loader_after_render,
  resolve_case_sla_label,
  resolve_case_sla_tone,
  show_onboarding_system_loader
} from '@/views/onboarding_system_views/onboarding_ui_helpers';
import {
  build_agreement_rows,
  build_case_form_sections,
  build_profile_rows,
  build_questionnaire_answers,
  build_stage_timeline,
  build_summary_rows,
  fetch_all_onboarding_cases,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';

function normalize_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function matches_active_journey(case_item = {}, journey_filter = '') {
  const normalized_filter = normalize_text(journey_filter);
  if (!normalized_filter) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => normalize_text(value) === normalized_filter);
}

function get_form_sections(form = {}) {
  return Array.isArray(form?.sections) ? form.sections : [];
}

function first_present_value(values = []) {
  return values.find((value) => String(value || '').trim()) || '';
}

function build_tab_id_for_section(form = {}, section = {}) {
  const form_id = first_present_value([form?.id, form?.form_name, 'section']);
  const section_id = first_present_value([section?.id, section?.label, 'item']);
  return `form-${form_id}-${section_id}`;
}

function tab_matches_profile(tab = {}) {
  const label = normalize_text(`${tab?.id || ''} ${tab?.label || ''}`);
  return ['profile', 'personal', 'applicant'].some((word) => label.includes(word));
}

function pick_reference_tab(tabs = []) {
  return tabs.find(tab_matches_profile) || tabs[0] || null;
}

export default {
  name: 'OnboardingManageApprovals',
  components: {
    AgreementSignModal
  },
  data: () => ({
    active_filter: 'all',
    is_loading_approvals: false,
    approvals_error: '',
    selected_approval: null,
    selected_case: null,
    active_detail_tab: 'summary',
    is_detail_fullscreen: false,
    sign_modal: {
      visible: false,
      agreement_title: 'Reseller agreement',
      signatory_name: '',
      document_id: 'DOC-APPROVAL-2026'
    },
    ...WORKFLOW_ACTION_STATE,
    filters: [
      { label: 'All', count: '0', tone: 'all' },
      { label: 'Open', tone: 'open' },
      { label: 'Mine', tone: 'mine' },
      { label: 'Blocked', tone: 'blocked' }
    ],
    detail_tabs: [
      { id: 'summary', label: 'Required Action: Approval Review', icon: 'bi-grid', badge: 'Current' },
      { id: 'agreements', label: 'Agreements', icon: 'bi-folder', count: '0' }
    ],
    summary_rows: [],
    profile_rows: [],
    stage_timeline: [],
    questionnaire_answers: [],
    case_form_sections: [],
    agreement_rows: [],
    approvals: [],
    ...tablePaginationState()
  }),
  computed: {
    ...workflowActionComputed,
    active_journey_filter() {
      return resolve_onboarding_journey_filter_key(this.$route);
    },
    filtered_approvals() {
      if (this.active_filter === 'open') {
        return this.filter_table_rows(this.approvals.filter((item) => item.status_tone !== 'success'));
      }

      if (this.active_filter === 'blocked') {
        return this.filter_table_rows(this.approvals.filter((item) => item.status_tone === 'warning'));
      }

      return this.filter_table_rows(this.approvals);
    },
    table_total_rows() {
      return this.filtered_approvals.length;
    },
    paginated_approvals() {
      return this.paginate_table_rows(this.filtered_approvals);
    },
    active_form_section() {
      const forms = Array.isArray(this.case_form_sections) ? this.case_form_sections : [];
      for (const form of forms) {
        const matched_section = get_form_sections(form)
          .find((section) => build_tab_id_for_section(form, section) === this.active_detail_tab);
        if (matched_section) return matched_section;
      }
      return null;
    },
    ...tablePaginationComputed
  },
  mounted() {
    this.load_approvals();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  watch: {
    active_journey_filter() {
      this.close_approval_detail();
      this.load_approvals();
    }
  },
  methods: {
    ...workflowActionMethods,
    handle_onboarding_data_updated() {
      this.load_approvals();
    },
    get_workflow_action_case() {
      return this.selected_case || null;
    },
    async on_workflow_action_success() {
      this.close_approval_detail();
      await this.load_approvals();
    },
    get_case_status(case_item) {
      return case_item.activation || case_item.stage || case_item.detail || 'In progress';
    },
    get_approval_state(case_item) {
      const sla_tone = resolve_case_sla_tone(case_item);
      return Object.fromEntries([
        ['status', this.get_case_status(case_item)],
        ['status_tone', case_item.activation_tone === 'ready'
          ? 'success'
          : case_item.activation_tone === 'blocked'
            ? 'danger'
            : sla_tone === 'success'
              ? 'success'
              : 'warning']
      ]);
    },
    map_case_to_approval(case_item) {
      const approval_state = this.get_approval_state(case_item);

      return Object.assign({}, {
        id: case_item.id,
        case_name: case_item.name,
        journey: case_item.journey || 'Onboarding',
        approver: case_item.owner || 'Unassigned',
        reason: case_item.stage || case_item.detail || 'Backend onboarding status',
        doa: case_item.application_id ? `APP-${case_item.application_id}` : 'APP',
        waited: resolve_case_sla_label(case_item),
        ...approval_state,
        case_item,
        activation: this.get_case_status(case_item)
      });
    },
    async load_approvals() {
      this.is_loading_approvals = true;
      this.approvals_error = '';
      show_onboarding_system_loader('Loading approvals...');

      try {
        const cases = await fetch_all_onboarding_cases();
        this.approvals = cases
          .filter((case_item) => matches_active_journey(case_item, this.active_journey_filter))
          .filter((case_item) => {
            const haystack = `${case_item.stage || ''} ${case_item.activation || ''}`.toLowerCase();
            return haystack.includes('approval') || haystack.includes('pending') || case_item.activation_tone === 'ready';
          })
          .map((case_item) => this.map_case_to_approval(case_item));
        this.filters = this.filters.map((filter) => (
          filter.tone === 'all' ? { ...filter, count: String(this.approvals.length) } : filter
        ));
      } catch (error) {
        this.approvals = [];
        this.approvals_error = error?.response?.data?.message || error?.message || 'Unable to load approvals.';
      } finally {
        this.is_loading_approvals = false;
        hide_onboarding_system_loader_after_render(this);
      }
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    tab_id_for_section(form, section) {
      return build_tab_id_for_section(form, section);
    },
    tab_icon_for_section(form = {}, section = {}) {
      const label = normalize_text(`${form?.form_name || ''} ${section?.label || ''}`);
      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => label.includes(word))
        ? 'bi-folder'
        : 'bi-list-check';
    },
    build_detail_tabs(form_sections = [], agreement_count = 0) {
      const section_tabs = [];
      form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          section_tabs.push({
            id: this.tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: this.tab_icon_for_section(form, section)
          });
        });
      });
      const reference_tab = pick_reference_tab(section_tabs);
      const tabs = [
        {
          id: 'summary',
          label: 'Required Action: Approval Review',
          icon: 'bi-grid',
          badge: 'Current'
        }
      ];
      if (reference_tab) {
        tabs.push({
          ...reference_tab,
          label: 'Application Reference',
          badge: 'Reference'
        });
      }
      section_tabs
        .filter((tab) => tab.id !== reference_tab?.id)
        .forEach((tab) => {
          tabs.push(tab);
        });
      tabs.push({ id: 'agreements', label: 'Agreements', icon: 'bi-folder', count: String(agreement_count) });
      return tabs;
    },
    async open_approval_detail(approval_item) {
      const case_item = approval_item?.case_item;
      if (!case_item) {
        return;
      }

      show_onboarding_system_loader('Loading approvals drawer...');
      this.reset_workflow_action_state();
      this.selected_approval = approval_item;
      this.selected_case = case_item;
      this.summary_rows = build_summary_rows(case_item);
      this.profile_rows = build_profile_rows(case_item);
      this.case_form_sections = build_case_form_sections(case_item);
      this.stage_timeline = build_stage_timeline(case_item);
      this.questionnaire_answers = build_questionnaire_answers(case_item);
      this.agreement_rows = build_agreement_rows(case_item);
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections, this.agreement_rows.length);
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;

      try {
        const hydrated_case = await hydrate_case_detail(case_item);
        this.selected_case = hydrated_case;
        this.summary_rows = build_summary_rows(hydrated_case);
        this.profile_rows = build_profile_rows(hydrated_case);
        this.case_form_sections = build_case_form_sections(hydrated_case);
        this.stage_timeline = build_stage_timeline(hydrated_case);
        this.questionnaire_answers = build_questionnaire_answers(hydrated_case);
        this.agreement_rows = build_agreement_rows(hydrated_case);
        this.detail_tabs = this.build_detail_tabs(this.case_form_sections, this.agreement_rows.length);
        await this.load_workflow_actions_for_case(hydrated_case);
      } catch {
        this.selected_case = case_item;
        await this.load_workflow_actions_for_case(case_item);
      } finally {
        hide_onboarding_system_loader_after_render(this);
      }
    },
    close_approval_detail() {
      this.reset_workflow_action_state();
      this.selected_approval = null;
      this.selected_case = null;
      this.case_form_sections = [];
      this.is_detail_fullscreen = false;
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    current_detail_action_tab() {
      return this.detail_tabs.find((tab) => String(tab?.badge || '').trim().toLowerCase() === 'current') || this.detail_tabs[0] || null;
    },
    current_detail_action_copy() {
      return 'Review the approval context, confirm the decision path, and approve, reject, or request more information.';
    },
    open_selected_approval_profile() {
      const first_section_tab = this.detail_tabs.find((tab) => !['summary', 'agreements'].includes(tab.id));
      this.active_detail_tab = first_section_tab?.id || 'summary';
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    open_sign_modal(agreement_title = 'Reseller agreement', signatory_name = '') {
      this.sign_modal = {
        visible: true,
        agreement_title,
        signatory_name: signatory_name || 'Onboarding signatory',
        document_id: this.selected_approval?.case_item?.id || 'DOC-APPROVAL-2026'
      };
    },
    close_sign_modal() {
      this.sign_modal.visible = false;
    },
    complete_signature() {
      this.close_sign_modal();
    },
    status_badge_class(tone) {
      if (tone === 'success') return 'bg-success-subtle text-success-emphasis';
      if (tone === 'danger') return 'bg-danger-subtle text-danger-emphasis';
      if (tone === 'warning') return 'bg-warning-subtle text-warning-emphasis';
      return 'bg-secondary-subtle text-secondary-emphasis';
    },
    activation_badge_class() {
      return this.selected_case?.activation_tone === 'ready'
        ? 'bg-success-subtle text-success-emphasis'
        : this.selected_case?.activation_tone === 'blocked'
          ? 'bg-danger-subtle text-danger-emphasis'
          : 'bg-warning-subtle text-warning-emphasis';
    },
    progress_bar_color() {
      return this.selected_case?.progress_tone === 'success' ? '#198754' : '#bf4b08';
    },
    answer_dot_class(tone) {
      if (tone === 'success') return 'bg-success';
      if (tone === 'warning') return 'bg-warning';
      return 'bg-secondary';
    }
  }
};
